import axios from '../utils/setupAxiosAuth';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import API_ROUTES from '../routes/routes';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('user_authToken')}`,
});

export interface InquiryWorkflowSummary {
  inquiry_id: number;
  broadcast_count: number;
  sent_count: number;
  reply_count: number;
  customer_contacted?: boolean | null;
  customer_approved?: boolean | null;
  approval_synced?: boolean;
  buyer_response_pending?: boolean;
}

export interface InquirySellerReply {
  id: number | null;
  inquiry_id: number;
  seller_id: number;
  seller_phone: string | null;
  seller_name: string | null;
  listed_price: number | null;
  listed_price_updated_at: string | null;
  broadcast_status: string | null;
  broadcast_sent_at: string | null;
  broadcast_id: number | null;
  raw_message: string | null;
  quoted_price: number | null;
  quantity_available: number | null;
  is_available: boolean | null;
  location: string | null;
  comments: string | null;
  parse_status: string | null;
  parse_confidence: number | null;
  message_count: number | null;
  llm_engine: string | null;
  llm_model: string | null;
  received_at: string | null;
  synced_at: string | null;
}

export interface InquirySellerBroadcast {
  id: number;
  inquiry_id: number;
  seller_id: number;
  seller_phone: string;
  seller_name: string | null;
  listed_price: number | null;
  delivery_status: string;
  sent_at: string | null;
  error_message: string | null;
}

const base = () => `${getApiBaseUrl()}${API_ROUTES.inquiryWorkflow}`;

export const fetchInquirySummaries = async (inquiryIds: number[]) => {
  if (!inquiryIds.length) return [] as InquiryWorkflowSummary[];
  const response = await axios.get(`${base()}/summaries`, {
    headers: authHeaders(),
    params: { ids: inquiryIds.join(',') },
  });
  return response.data as InquiryWorkflowSummary[];
};

/** Inquiry ids where buyer WhatsApp awaits staff reply (optional status tab filter). */
export const fetchPendingInquiryIds = async (status?: string) => {
  const response = await axios.get(`${base()}/pending-inquiry-ids`, {
    headers: authHeaders(),
    params: status ? { status } : undefined,
  });
  return (response.data?.inquiry_ids || []) as number[];
};

export const fetchInquiryReplies = async (inquiryId: number) => {
  const response = await axios.get(`${base()}/${inquiryId}/replies`, {
    headers: authHeaders(),
  });
  return response.data as InquirySellerReply[];
};

export const fetchInquiryBroadcasts = async (inquiryId: number) => {
  const response = await axios.get(`${base()}/${inquiryId}/broadcasts`, {
    headers: authHeaders(),
  });
  return response.data as InquirySellerBroadcast[];
};

export const syncInquiryRepliesFromPlivo = async (
  inquiryId: number,
  options: { runProviderSync?: boolean; syncDays?: number } = {}
) => {
  const response = await axios.post(
    `${base()}/${inquiryId}/sync-replies`,
    {
      runTwilioSync: options.runProviderSync !== false,
      twilioSyncDays: options.syncDays,
    },
    { headers: authHeaders() }
  );
  return response.data as {
    synced: number;
    parsed: number;
    broadcasts: number;
    new_messages?: number;
    window_start?: string;
    window_end?: string;
    last_sync_at?: string;
    incremental?: boolean;
    replies: InquirySellerReply[];
  };
};

/** @deprecated use syncInquiryRepliesFromPlivo */
export const syncInquiryRepliesFromTwilio = syncInquiryRepliesFromPlivo;

export const pollTwilioForInquiryReplies = async (days = 1) => {
  const response = await axios.post(
    `${base()}/poll-twilio`,
    { days },
    { headers: authHeaders() }
  );
  return response.data as {
    days: number;
    twilioSync?: {
      fetched?: number;
      inquiryRepliesProcessed?: number;
      error?: string;
    };
  };
};

/** Fast inbox refresh — processes DB-stored WhatsApp (no Twilio API pull). */
export const refreshInquiryRepliesFromInbox = async (inquiryId: number) => {
  const response = await axios.post(
    `${base()}/${inquiryId}/refresh-replies`,
    {},
    { headers: authHeaders() }
  );
  return response.data as {
    processed: number;
    updated: number;
    broadcasts: number;
    buyer_processed?: number;
    buyer_candidates?: number;
    customer_notes?: string | null;
    customer_contacted?: boolean;
    customer_approved?: boolean | null;
    replies: InquirySellerReply[];
  };
};

export interface BuyerWhatsAppSession {
  can_send: boolean;
  /** Utility template when outside 24h session (Plivo). */
  can_send_template?: boolean;
  can_send_any?: boolean;
  delivery_mode?: 'session' | 'template' | null;
  buyer_phone?: string | null;
  last_inbound_at?: string | null;
  session_expires_at?: string | null;
  reason?: string | null;
  /** Buyer WhatsApp arrived after our last staff/buyer-template outbound for this inquiry. */
  response_pending?: boolean;
  last_outbound_at?: string | null;
  last_inbound_preview?: string | null;
}

export const fetchBuyerWhatsAppSession = async (inquiryId: number) => {
  const response = await axios.get(`${base()}/${inquiryId}/buyer-whatsapp/session`, {
    headers: authHeaders(),
  });
  return response.data as BuyerWhatsAppSession;
};

export const sendBuyerWhatsAppMessage = async (inquiryId: number, body: string) => {
  const response = await axios.post(
    `${base()}/${inquiryId}/buyer-whatsapp`,
    { body },
    { headers: authHeaders() }
  );
  return response.data as {
    ok: boolean;
    customer_notes?: string | null;
    customer_contacted?: boolean;
    message?: {
      provider_message_id?: string;
      body?: string;
      sent_at?: string;
    };
  };
};
